import { Outlet, NavLink } from 'react-router-dom';
import { useContractorStore } from '../../contexts/contractorStore';
import {
  Briefcase,
  Map,
  User,
  Wifi,
  WifiOff,
  RefreshCw,
} from 'lucide-react';
import { useState, useEffect } from 'react';

export function ContractorLayout() {
  const { account } = useContractorStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount] = useState(0); // TODO: Connect to sync service

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const navItems = [
    { path: '/contractor/dashboard', label: 'Jobs', icon: Briefcase, end: true },
    { path: '/contractor/map', label: 'Map', icon: Map },
    { path: '/contractor/profile', label: 'Profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Minimal Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between safe-area-top">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-white">
            Contractor
          </span>
          {account && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {account.firstName}
            </span>
          )}
        </div>

        {/* Sync Status Indicator */}
        <div className="flex items-center gap-2">
          {pendingSyncCount > 0 && (
            <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-xs font-medium">{pendingSyncCount}</span>
            </div>
          )}
          {isOnline ? (
            <Wifi className="w-5 h-5 text-green-500" />
          ) : (
            <WifiOff className="w-5 h-5 text-red-500" />
          )}
        </div>
      </header>

      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-amber-500 text-white text-center text-sm py-1 px-4">
          You're offline. Changes will sync when connected.
        </div>
      )}

      {/* Main Content Area - scrollable */}
      <main className="flex-1 overflow-auto pb-20">
        <Outlet />
      </main>

      {/* Bottom Navigation - Fixed */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-bottom z-50">
        <div className="flex justify-around items-center h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center w-full h-full min-h-[44px] transition-colors ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`
              }
            >
              <item.icon className="w-6 h-6" />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default ContractorLayout;
